// @ts-check
import Vue from 'vue';
import { BETSLIP_LAYOUTS, BETSLIP_TYPES } from 'bto-models/betslip/const';
import { getLayout, getSelectedTab } from 'bto-models/betslip/mapper';
import PlaceModel from 'bto-models/betslip/place.model';

import { debounce } from '@bto-services/utils';
import { userLoggedIn, fetchCalculateBonus } from '@bto-services/user';

import { getCombsAtFold } from './combs';
import { getTotalMatchItems } from './items';
import { state } from '../state';
import { saveState } from './helpers';
import { configStore } from './config.store';

import { specialBetCombStore } from './special-bet-comb.store';

/**
 * @typedef {import('bto-models/betslip/bonuses/acumulator-model').default} Acumulator
 * @typedef {import('bto-models/betslip/bonuses/acumulator-bonus-model').default} AcumulatorBonusModel
 */

/**
 * Get tax settings from global settings
 * @returns {Partial<typeof APP_SETTINGS.taxRules>}
 */
function getTaxSettings() {
	return (!userLoggedIn() && APP_SETTINGS.taxRules) || {};
}

/**
	* Calculate stake tax if setting for taxPerStake is set otherwiser return the original stake
	* @param {number} stake - original stake
	*/
function calcuateStakeTax(stake) {
	const taxSettings = getTaxSettings();
	if (!taxSettings.useTaxAnonymousUser) {
		return stake;
	}
	const perStakeTax = (taxSettings.taxPerStake || 0) / 100;
	return stake - (perStakeTax * stake);
}

/**
 * Calculate winnings tax if setting for taxForWinning is set and winnings is above tax min winning limit
 * @param {number} winnings - original winnings
 */
function calculateWinningTax(winnings) {
	const taxSettings = getTaxSettings();
	if (!taxSettings.useTaxAnonymousUser) {
		return 0;
	}
	const perWinningsTax = (taxSettings.taxForWinning || 0) / 100;
	const taxMinWinningLimit = taxSettings.minWinningsLimit || 0;
	const winningsTax = perWinningsTax * winnings;
	return winnings > taxMinWinningLimit ? winningsTax : 0;
}

/**
 * Calculate bonus for given winnings
 * @param {AcumulatorBonusModel} appliedBonus - Applied acumulator bonus model if any
 * @param {Acumulator} acumulator - Bonuses acumulator model
 * @param {number} stake - Stake amount
 * @param {number} winnings - Winning amount
 */
function calculateBonus(appliedBonus, acumulator, stake, winnings) {
	if (appliedBonus) {
		const netStake = acumulator.bonusOnPotentialWinningAmount ? 0 : stake;
		const maxBonus = (winnings - netStake) * (appliedBonus.bonusPercentage / 100);
		return acumulator.maxBonus && acumulator.maxBonus <= maxBonus ? acumulator.maxBonus : maxBonus;
	}
	return 0;
}

/**
 * Check if given number is inifinity, if it is return 0 otherwise return the number
 * @param {number} num - number to check
 */
function checkInfinity(num) {
	return num === Infinity ? 0 : num;
}

/**
 * Format number to 2 decimal places
 * @param {number} num - number to check
 */
function roundNumber(num) {
	return num.toFixed(2);
}

/**
 * Calculate winnings and bonuses for all combinations
 * @param {{odds: number[],stake:number}[]} combs - List of bet item combinations
 * @param {boolean} [forceWinningsCalculation] - Optional flag if we should skip winnings and bonuses caluclations
 */
function calculateWinnings(combs, forceWinningsCalculation = false) {
	const acumulator = configStore.accumulatorBonus;
	const allBonuses = acumulator.accBonuses;
	const addStakeAfterTax = configStore.settings.ADD_STAKE_TAX;

	let calcs = combs.reduce((acc, comb) => {
		const comboOdds = comb.odds;
		const comboTotalOdd = comboOdds.reduce((pro, val) => pro *= val, 1);
		acc.minOdds = Math.min(acc.minOdds, comboTotalOdd);
		acc.maxOdds = Math.max(acc.maxOdds, comboTotalOdd);
		acc.sumStake += comb.stake;
		// If user is logged stop calculations here
		if (userLoggedIn() && !forceWinningsCalculation) {
			return acc;
		}

		const comboTaxedStake = calcuateStakeTax(comb.stake);
		acc.sumStakeTax += comb.stake - comboTaxedStake;

		const comboStake = addStakeAfterTax ? comb.stake : comboTaxedStake;

		const comboWinnings = (+comboTotalOdd.toFixed(2)) * comboStake;
		const comboTax = calculateWinningTax(comboWinnings);
		// Sum of max winnings
		acc.maxWin += comboWinnings;
		acc.sumTax += comboTax;

		const appliedBonus = allBonuses.find(b => b.minOdd <= (comboOdds[b.minSelections - 1] || 0));
		const comboBonus = calculateBonus(appliedBonus, acumulator, comboStake, comboWinnings);
		acc.maxBonus += comboBonus;
		// Get the min winning
		if (comboWinnings < acc.minWin) {
			acc.minWin = comboWinnings;
		}
		// Get the min tax
		if (comboTax < acc.minTax) {
			acc.minTax = Math.round(comboTax);
		}
		acc.minBonus = comboBonus < acc.minBonus ? comboBonus : acc.minBonus;
		return acc;
	}, {
		sumTax: 0,
		sumStakeTax: 0,
		sumStake: 0,
		maxWin: 0,
		maxBonus: 0,
		maxOdds: 0,
		minOdds: Infinity,
		minWin: Infinity,
		minBonus: Infinity,
		minTax: Infinity,
	});

	// Set posible Infinity calcs to 0
	['minOdds', 'minWin', 'minBonus', 'minTax'].forEach(k => {
		calcs[k] = checkInfinity(calcs[k]);
	});

	// Round calcs
	['minOdds', 'maxOdds'].forEach(k => {
		calcs[k] = roundNumber(calcs[k]);
	});
	// Round total tax amount to integer
	calcs.sumTax = Math.round(calcs.sumTax);
	// Add bonuses and subtrackt taxes from potential winnings
	calcs.maxWin = calcs.maxWin + calcs.maxBonus - calcs.sumTax;
	calcs.minWin = calcs.minWin + calcs.minBonus - calcs.minTax;

	return calcs;
}

/**
 * Get odds from combination
 * @param {string[]} comb - combination
 */
const getCombOrderedOdds = (comb) => {
	return comb.map(itemKey => {
		// If item is race odd get the odd from raceOdd
		if (!state.items[itemKey] && itemKey.endsWith('_RO')) {
			const ik = itemKey.replace('_RO', '');
			return +state.items[ik].raceOdd.odd;
		}
		return +state.items[itemKey].odd;
	}).sort((a, b) => b - a);
}

/**
 * Get odds for singles item
 * @param {string} itemKey - bet items key
 */
function getSingleCombs(itemKey) {
	let acc = [];
	let singleOdd = Number(state.items[itemKey].odd);
	let stake = +state.singles[itemKey];
	acc.push({ odds: [singleOdd], stake });
	if (state.raceOdds[itemKey]) {
		const raceOdd = Number(state.items[itemKey].raceOdd.odd);
		acc.push({ odds: [raceOdd], stake });
	}
	return acc;
}

/**
 * Calculate bonuses for single item
 * @param {string} itemKey - calculate bonuses for single item
 */
export function calculateSingleBonus(itemKey) {
	if (state.singles[itemKey]) {
		const combs = getSingleCombs(itemKey);
		const totals = calculateWinnings(combs, true);
		Vue.set(state.singleBonuses, itemKey, totals.maxWin);
		return;
	}
	Vue.delete(state.singleBonuses, itemKey);
}
/**
 * Get all combinations and their totals for given fold
 * @param {string} fold - fold key
 * @returns array of combinations with totals info for each comb 
 */
export function getCombsDetails(fold) {
	const k = +fold;
	const foldStake = (+state.folds[fold]);
	const items = Object.keys(state.items);
	const raceItems = Object.keys(state.raceOdds).map(r => `${r}_RO`);
	const bankers = state.bankers;
	const allItems = items.concat(raceItems);
	const sbcFold = fold.replace('sbc', '');
	let sbcFoldStake = 0;

	const itemsMap = items.reduce((acc, val, i) => {
		acc[val] = i + 1;
		return acc;
	}, {});
	let systemCombs = [];
	if (fold === '-1') {
		systemCombs = Object.keys(state.foldLines).flatMap((fl) => getCombsAtFold(allItems, +fl, bankers));
	} else if (fold !== sbcFold) {
		sbcFoldStake = +specialBetCombStore.stakes[sbcFold];
		systemCombs = specialBetCombStore.specialBetCombs[sbcFold].inclCombinations.flatMap((fl) => getCombsAtFold(allItems, +fl, bankers));
	} else {
		systemCombs = getCombsAtFold(allItems, k, bankers);
	}

	return systemCombs.map(comb => {
		const odds = getCombOrderedOdds(comb);
		const totals = calculateWinnings([{ odds, stake: sbcFoldStake || foldStake }], true);
		const combs = comb.map(c => itemsMap[c.replace('_RO', '')]).join(',');
		return { combs, maxOdds: totals.maxOdds, maxWin: totals.maxWin };
	});
}

// FEAT: add debounce method here? maybe use web worker?
export const calculateBonuses = debounce(() => {
	let combs = [];
	state.foldOdds = {};
	const itemsCount = Object.keys(state.items).length;
	const selectedLayout = getLayout(itemsCount, state.freebetId);
	const selectedTab = getSelectedTab(state);

	if (selectedLayout === BETSLIP_LAYOUTS.EUROPEAN || selectedTab === BETSLIP_TYPES.SINGLE) {
		Object.keys(state.singles).reduce((acc, itemKey) => {
			const singleCombs = getSingleCombs(itemKey);
			acc.push(...singleCombs);
			return acc;
		}, combs);
	}
	if (selectedLayout === BETSLIP_LAYOUTS.EUROPEAN || selectedTab === BETSLIP_TYPES.SYSTEM) {
		const items = Object.keys(state.items);
		const raceItems = Object.keys(state.raceOdds).map(r => `${r}_RO`);
		const bankers = state.bankers;
		const allItems = items.concat(raceItems);

		Object.keys(state.folds).reduce((acc, fold) => {
			if (fold === '-1') {
				return acc;
			}
			const k = +fold;
			const foldStake = (+state.folds[fold]);

			let systemCombs = getCombsAtFold(allItems, k, bankers);
			systemCombs.forEach(comb => {
				const odds = getCombOrderedOdds(comb);
				acc.push({ odds, stake: foldStake });
				// If single line combination keep total odd for it in store
				if (systemCombs.length === 1) {
					const foldTotalOdd = roundNumber(odds.reduce((pro, val) => pro *= val, 1));
					Vue.set(state.foldOdds, fold, foldTotalOdd);
				}
			});

			return acc;
		}, combs);

		// Set single line folds total odds
		Object.keys(state.foldLines)
			.filter(k => !state.folds[k] && state.foldLines[k] === 1)
			.forEach(fold => {
				const k = +fold;
				const systemCombs = getCombsAtFold(allItems, k, bankers);
				const odds = getCombOrderedOdds(systemCombs[0]);
				const foldTotalOdd = roundNumber(odds.reduce((pro, odd) => pro *= odd, 1));
				Vue.set(state.foldOdds, fold, foldTotalOdd);
			});
	}
	if (selectedLayout === BETSLIP_LAYOUTS.TABS && selectedTab === BETSLIP_TYPES.COMBI) {
		const items = Object.keys(state.items);
		const raceItems = Object.keys(state.raceOdds).map(r => `${r}_RO`);
		const matchNum = getTotalMatchItems();
		const allItems = items.concat(raceItems);
		const comboStake = (+state.folds[matchNum]) || 0;
		let comboCombs = getCombsAtFold(allItems, matchNum, {});
		comboCombs.forEach(comb => {
			const odds = getCombOrderedOdds(comb);
			if (comboStake) {
				combs.push({ odds, stake: comboStake });
			}
			// If single line combination keep total odd for it in store
			if (comboCombs.length === 1) {
				const foldTotalOdd = roundNumber(odds.reduce((pro, val) => pro *= val, 1));
				Vue.set(state.foldOdds, matchNum, foldTotalOdd);
			}
		});
	}
	const totals = calculateWinnings(combs);

	state.totals = {
		stake: totals.sumStake,
		maxOdds: totals.maxOdds,
		minOdds: totals.minOdds,
	};

	state.bonuses = {
		maxWin: totals.maxWin,
		minWin: totals.minWin,
		maxBonus: totals.maxBonus,
		minBonus: totals.minBonus,
		sumTax: totals.sumTax,
		sumStakeTax: totals.sumStakeTax,
	};

	state.calculationInProgress = false;

	if (userLoggedIn() && !state.quickBetActive) {
		const placeModel = new PlaceModel(state);
		fetchCalculateBonus(placeModel).then(( /**@type {{data: import('bto-models/betslip/bonuses/api').TCalculateBonus}} */resp) => {
			if (!resp.data) {
				return;
			}
			if (state.netFreebet && resp.data.MaxWinnings) {
				resp.data.MaxWinnings = resp.data.MaxWinnings - state.totals.stake;
				resp.data.MinWinnings = resp.data.MinWinnings - state.totals.stake;
			}
			const sbBonuses = {
				maxWin: resp.data.MaxWinnings,
				minWin: resp.data.MinWinnings,
				maxBonus: resp.data.MaxBonusAmount,
				minBonus: resp.data.MinBonusAmount,
				sumTax: resp.data.SumPayoutTax,
				sumStakeTax: resp.data.StakeAfterTaxAmount,
			};
			state.bonuses = sbBonuses;
			saveState();
		}).catch(() => { });
		return;
	}
	saveState();
}, 300);